/**
 * Centralized color constants for the application.
 *
 * Usage:
 * - In styled-components: use theme.palette.x (preferred)
 * - In inline styles / component props: import { colors } from 'theme/colors'
 * - In MUI sx prop: use shorthand like 'text.primary'
 */

export const colors = {
  // Primary brand color
  primary: {
    main: '#0772E5',
    light: '#4BA0FD',
    dark: '#101d31',
    contrastText: '#FFFFFF',
  },

  // Secondary brand color
  secondary: {
    main: '#9C27B0',
    light: '#BA68C8',
    dark: '#7B1FA2',
    contrastText: '#FFFFFF',
  },

  // Error / Danger colors
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
    contrastText: '#FFFFFF',
  },

  // Warning colors
  warning: {
    main: '#FC9B00',
    light: '#FFB74D', // not used
    dark: '#F57C00',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },

  // Success colors
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
    contrastText: '#FFFFFF',
  },

  // Info colors
  info: {
    main: '#4285F4',
    light: '#64B5F6',
    dark: '#1976D2',
    contrastText: '#FFFFFF',
  },

  // Text colors (Material Design standard)
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: 'rgba(0, 0, 0, 0.38)',
  },

  // Background colors
  background: {
    default: '#FAFAFA',
    paper: '#FFFFFF',
    grey: '#F5F5F5',
  },

  // Action colors (for interactive elements)
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 0, 0, 0.04)',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
  },

  // Divider
  divider: 'rgba(0, 0, 0, 0.12)',

  // Common colors
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },

  // Grey palette
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // Sidebar colors
  sidebar: {
    main: '#2c446c', // Main icon bar and company logo area
    submenu: '#223b65', // Expanded submenu area
    itemSelected: '#0160c7', // Selected submenu item
    itemHover: '#3d5378', // Hover on non-selected submenu item
  },

  // Shadow colors (must use rgba for proper transparency)
  shadows: {
    light: 'rgba(0, 0, 0, 0.07)', // Subtle shadow
    medium: 'rgba(0, 0, 0, 0.12)', // Standard shadow
    dark: 'rgba(0, 0, 0, 0.20)', // Strong shadow
    // Standard Material Design elevation shadow components
    elevation: {
      umbra: 'rgba(0, 0, 0, 0.2)',
      penumbra: 'rgba(0, 0, 0, 0.14)',
      ambient: 'rgba(0, 0, 0, 0.12)',
    },
    // Specific shadow presets
    card: '0px 8px 50px 0px rgba(7, 5, 26, 0.07)',
    dropdown:
      '0px 5px 6px -3px rgba(0, 0, 0, 0.20), 0px 9px 12px 1px rgba(0, 0, 0, 0.14), 0px 3px 16px 2px rgba(0, 0, 0, 0.12)',
    paper:
      'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
    6: '0px 8px 50px 0px rgba(7, 5, 26, 0.07)',
    8: '0px 8px 50px 0px rgba(7, 5, 26, 0.07)',
    24: '0px 8px 50px 0px rgba(7, 5, 26, 0.07)',
  },

  // Overlay colors (for hover/focus states on dark backgrounds)
  overlay: {
    light: 'rgba(255, 255, 255, 0.08)', // Subtle white overlay
    medium: 'rgba(255, 255, 255, 0.12)', // Standard white overlay
    strong: 'rgba(255, 255, 255, 0.16)', // Strong white overlay
    border: 'rgba(255, 255, 255, 0.12)', // Border on dark backgrounds
  },

  // Tinted backgrounds (subtle color backgrounds)
  tint: {
    primary: {
      subtle: 'rgba(7, 114, 229, 0.04)', // Very light primary bg
      light: 'rgba(7, 114, 229, 0.08)', // Light primary bg
      medium: 'rgba(7, 114, 229, 0.12)', // Medium primary bg
    },
    warning: {
      subtle: 'rgba(237, 108, 2, 0.04)',
      light: 'rgba(237, 108, 2, 0.08)',
      medium: 'rgba(237, 108, 2, 0.12)',
    },
    error: {
      subtle: 'rgba(211, 47, 47, 0.04)',
      light: 'rgba(211, 47, 47, 0.08)',
      medium: 'rgba(211, 47, 47, 0.12)',
      border: 'rgba(211, 47, 47, 0.4)', // Border around a medium-tinted surface
    },
    success: {
      subtle: 'rgba(76, 175, 80, 0.04)',
      light: 'rgba(76, 175, 80, 0.08)',
      medium: 'rgba(76, 175, 80, 0.12)',
      border: 'rgba(76, 175, 80, 0.4)', // Border around a medium-tinted surface
    },
  },

  // Toast background colors (very subtle tints)
  toast: {
    success: {
      background: 'rgba(233, 242, 234, 1)', // Very light green
      text: 'rgba(46, 125, 50, 1)',
    },
    info: {
      background: 'rgba(229, 243, 250, 1)', // Very light blue
      text: 'rgba(7, 114, 229, 1)',
    },
    warning: {
      background: 'rgba(253, 240, 230, 1)', // Very light orange
      text: '#5f2b01',
    },
    error: {
      background: 'rgba(251, 234, 234, 1)', // Very light red
      text: 'rgba(211, 47, 47, 1)',
    },
  },

  // Legacy palette overrides (backward compatibility with older color values)
  // These differ from the standard MUI palette values above
  legacy: {
    secondary: {
      main: '#F1F0F0',
      100: '#555555',
    },
    grey: {
      200: '#636363',
      400: '#E0E1E9',
      500: '#9B9B9B',
    },
  },

  // App-specific UI colors
  ui: {
    snackbar: {
      success: '#78C925',
      warning: '#F5A623',
      info: '#4285F4',
      error: '#FF0000',
    },
    profileAndSettings: {
      passwordChanged: '#7ED321',
    },
    select: {
      arrowDropDown: 'rgba(0, 0, 0, 0.54)',
    },
    avatar: {
      background: '#B616E4',
    },
    chip: {
      success: '#4BBD00',
      error: '#F30000',
      border: '#D3D6D9',
      grayText: '#6D7883',
      text: '#0772E5',
    },
    alarm: {
      main: '#E20101',
      mainHover: '#a80000',
      amber: '#FC9B00',
      amberHover: '#c27802',
    },
    icon: {
      background: {
        secondary: '#E2E4E6',
        primary: '#0772E51A',
      },
    },
    border: {
      light: '#C0C7D3',
      main: '#D3D6D9',
    },
    luzmo: {
      loaderBackground: '#edecf2',
    },
    status: {
      overdue: 'rgba(211, 47, 47, 0.30)',
      inProgress: 'rgba(237, 108, 2, 0.3)',
      completed: '#C0D8C1',
    },
    risk: {
      low: '#37B44A',
      lowAlt: '#31913A',
      medium: '#F9EB11',
      high: '#FFC554',
      veryHigh: '#EB1520',
      extreme: '#BA1417',
    },
    // the four values the legacy audit module uses, so a score keeps its colour at cutover
    score: {
      poor: '#E65100',
      fair: '#FBC02D',
      good: '#0772E5',
      strong: '#2e7d32',
    },
    // the ten colours a choice option can carry; the builder stores the key, fill-out and the record tint from it
    optionColour: {
      success: '#4CAF50',
      teal: '#009688',
      info: '#4285F4',
      indigo: '#3F51B5',
      purple: '#9C27B0',
      pink: '#E91E63',
      error: '#D32F2F',
      warning: '#FC9B00',
      yellow: '#F9C900',
      brown: '#795548',
    },
  },
} as const;

export type Colors = typeof colors;
